import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useRef, useState } from 'react';
import { Button, GridRow, GridItem, Icon, Link, Skeleton, Spacer, Typography, useSnackbar } from '@emburse/embark-core';
import { LaunchIcon } from '@emburse/embark-icons';
import { getDataInterchangeConfig, createDataInterchangeConfig, updateDataInterchangeConfig, getDITypeConfiguration, getDataInterchangeTypeStatues } from '../../api/dataInterchangeconfig';
import { getDataInterchangeDirectories, getDataInterchanges } from '../../api/dataInterchange';
import { DataInterchangeConfig, DITypeConfig } from '../../interfaces/DataInterchangeConfig';
import { logger } from '../../util/logger';
import { getQueryStringParam } from '../../util/helpers';
import SingleColumnLayout from '../../components/Layouts/SingleColumnLayout';
import { CustomStepper } from '../../components';
import CorpDataInterchangeConfigStep1 from './CorpDataInterchangeConfigStep1';
import CorpDataInterchangeConfigStep2 from './CorpDataInterchangeConfigStep2';
import CorpDataInterchangeConfigStep3 from './CorpDataInterchangeConfigStep3';
import CorpDataInterchangeConfigModal from './CorpDataInterchangeConfigModal';
import CorpDataInterchangeConfigStep3Provider from './CorpDataInterchangeConfigStep3Provider';
import { $t, useTranslation } from '../../hooks/useTranslation';
import useLoadingOverlay from '../../hooks/useLoadingOverlay';
import { validateEmail } from '../../util/formValidation';
export const mapIndexToAlphabet = (value) => {
    const alphabet = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const alphabetArray = alphabet.split('');
    if (value > 75) {
        return (alphabetArray[value - 76] +
            alphabetArray[value - 76] +
            alphabetArray[value - 76] +
            alphabetArray[value - 76]);
    }
    if (value > 50) {
        return alphabetArray[value - 51] + alphabetArray[value - 51] + alphabetArray[value - 51];
    }
    if (value > 25) {
        return alphabetArray[value - 26] + alphabetArray[value - 26];
    }
    return alphabetArray[value];
};
var AttributeValues;
(function (AttributeValues) {
    AttributeValues[AttributeValues["Employee"] = 1] = "Employee";
    AttributeValues[AttributeValues["Manager"] = 2] = "Manager";
    AttributeValues[AttributeValues["Executive"] = 3] = "Executive";
    AttributeValues[AttributeValues["Accountant"] = 4] = "Accountant";
})(AttributeValues || (AttributeValues = {}));
const mapAdditionalAttributes = (attrs) => {
    if (!attrs) {
        return {};
    }
    const attrsArray = attrs.split(',');
    const rolesArray = ['Employee', 'Manager', 'Executive', 'Accountant'];
    const AdditionalAttribute = {
        Employee: '',
        Manager: '',
        Executive: '',
        Accountant: ''
    };
    attrsArray.forEach((attr) => {
        const keyVal = attr.split(':');
        if (rolesArray.includes(keyVal[0])) {
            return;
        }
        if (!AdditionalAttribute[AttributeValues[keyVal[1]]]) {
            AdditionalAttribute[AttributeValues[keyVal[1]]] += keyVal[0];
        }
        else {
            AdditionalAttribute[AttributeValues[keyVal[1]]] += `, ${keyVal[0]}`;
        }
    });
    return { AdditionalAttribute };
};
const setMapData = (data) => {
    const mapData = [];
    data.NestedNodes.forEach((node) => {
        const attr = node.Attributes;
        mapData.push({
            id: parseInt(attr.sourceindex, 10),
            Column: mapIndexToAlphabet(parseInt(attr.sourceindex, 10)),
            Example: '',
            CertifyField: attr.target,
            ...mapAdditionalAttributes(attr?.values)
        });
    });
    return mapData;
};
const defaultAttributes = 'Employee:1,Manager:2,Executive:3,Accountant:4';
const configureAdditionalAttribute = (data) => {
    if (!data) {
        return {};
    }
    const value = Object.entries(data)
        .map((entryArray) => {
        const key = entryArray[0];
        const roleString = entryArray[1];
        const roles = roleString.split(',');
        return roles.map((role) => `${role.trim()}:${AttributeValues[key.trim()]}`);
    })
        .join(',');
    return { values: `${defaultAttributes},${value}` };
};
const readFile = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsText(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = (error) => reject(error);
});
const typeMap = () => ({
    'Department Sync': 'Dept',
    'User Sync': 'User',
    'Employee GLD 1 Sync': 'EmpUDF1',
    'Employee GLD 2 Sync': 'EmpUDF2',
    'Employee GLD 3 Sync': 'EmpUDF3',
    'Employee GLD 4 Sync': 'EmpUDF4',
    'Employee GLD 5 Sync': 'EmpUDF5',
    'Expense Report GLD 1 Sync': 'ExpRptUDF1',
    'Expense Report GLD 2 Sync': 'ExpRptUDF2',
    'Expense Report GLD 3 Sync': 'ExpRptUDF3',
    'Expense Report GLD 4 Sync': 'ExpRptUDF4',
    'Expense Report GLD 5 Sync': 'ExpRptUDF5'
});
const transformInterchangeData = (row) => {
    const transformedRow = row;
    transformedRow.id = transformedRow.PK;
    return transformedRow;
};
const transformSFTPPaths = (row, i) => {
    const transformedRow = row;
    transformedRow.id = i;
    transformedRow.Name = transformedRow.Path;
    transformedRow.Status = 'Unknown';
    return transformedRow;
};
const erasedMapData = (mapData) => {
    const updatedMapData = mapData.map((item) => {
        if (item.CertifyField) {
            // eslint-disable-next-line no-param-reassign
            item.CertifyField = ' ';
        }
        return item;
    });
    return updatedMapData;
};
function CorpDataInterchangeConfig() {
    useTranslation();
    const { applyLoadingState, removeLoadingState } = useLoadingOverlay();
    const customStepperRef = useRef(null);
    const didMount = useRef(false);
    const additionalActions = useRef([]);
    const [showModal, setShowModal] = useState(false);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [sftpPaths, setSFTPPaths] = useState([]);
    const [DIConfig, setDIConfig] = useState(new DITypeConfig({}));
    const [interchangeTypes, setInterchangeTypes] = useState([]);
    /* ******** STEPS 1 & 3 STATE AND METHODS ******** */
    const [importRecords, setImportRecords] = useState({
        type: 'importrecords',
        table: '', // Interchange Type - Step 1
        key: '', // Key - Step 1
        delimiter: ',', // Step 3
        header: false, // Step 3
        deactivateabsent: false, // Step 1
        notificationemails: '', // Emails - Step 1
        keyondeptname: false, // User Dept Key - Step 1
        mapData: [] // Either config this from NestedNodes, or use NestedNodes,
    });
    const { enqueueSnackbar } = useSnackbar();
    const handleError = useCallback((err) => {
        enqueueSnackbar(err.message, { variant: 'alert', AlertProps: { severity: 'error' } });
        removeLoadingState();
        logger.log(err);
    }, [enqueueSnackbar, removeLoadingState]);
    const setDefaultAttributes = useCallback((config) => {
        if (!window.location.search) {
            const attributes = {};
            const attributeMap = {
                'Record Key': 'key',
                'Notification Emails': 'notificationemails',
                'Do Not Send Welcome Email': 'donotsendwelcomeemail',
                'Deactivate Absents': 'deactivateabsent',
                'Commit Changes': 'commitchanges',
                'Key on Department Name': 'keyondeptname'
            };
            const keys = Object.keys(attributeMap);
            config?.mappingAction?.Attributes.forEach((item) => {
                if (keys.includes(item.Name) && item.ShowInUI) {
                    let value = item.DefaultValue;
                    if (item.DefaultValue.toLowerCase() === 'true') {
                        value = true;
                    }
                    if (item.DefaultValue.toLowerCase() === 'false') {
                        value = false;
                    }
                    if (item.Name === 'Notification Emails') {
                        attributes[attributeMap[item.Name]] =
                            value.toString().length > 0 ? value : window.AccountConfig.Emp.Email;
                    }
                    else {
                        attributes[attributeMap[item.Name]] = value;
                    }
                }
            });
            setImportRecords((prevState) => ({
                ...prevState,
                ...attributes,
                ...{ mapData: erasedMapData(prevState.mapData) }
            }));
        }
    }, []);
    const getTypeConfig = useCallback((type) => {
        applyLoadingState(30000);
        getDITypeConfiguration(type)
            .then((data) => {
            const updatedData = JSON.parse(JSON.stringify(data));
            setDIConfig(updatedData);
            setDefaultAttributes(updatedData);
            setLoading(false);
            removeLoadingState();
        })
            .catch(handleError);
    }, [applyLoadingState, handleError, removeLoadingState, setDefaultAttributes]);
    const updateImportRecords = useCallback((data) => {
        let updatedData = data;
        if (data?.table) {
            updatedData =
                data.table === 'User'
                    ? { ...updatedData, ...{ type: 'importusersadv' } }
                    : { ...updatedData, ...{ type: 'importrecords' } };
            getTypeConfig(data.table);
        }
        setImportRecords((prevState) => ({ ...prevState, ...updatedData }));
    }, [getTypeConfig]);
    const setImportRecordsData = useCallback((data) => {
        const attr = data.Attributes;
        attr.deactivateabsent = attr.deactivateabsent.toLowerCase() === 'true';
        attr.header = attr.header.toLowerCase() === 'true';
        if (attr.type === 'importusersadv') {
            attr.commitchanges = attr.commitchanges.toLowerCase() === 'true';
            attr.donotsendwelcomeemail = attr.donotsendwelcomeemail.toLowerCase() === 'true';
            attr.keyondeptname = attr.keyondeptname.toLowerCase() === 'true';
        }
        attr.mapData = setMapData(data);
        updateImportRecords(attr);
    }, [updateImportRecords]);
    /* ******** END STEPS 1 & 3 STATE AND METHODS ******** */
    /* ******** STEP 2 STATE AND METHODS ******** */
    const [fileConfig, setFileConfig] = useState({
        host: 'sftp.certify.com',
        file: '',
        path: '',
        filePattern: ''
    });
    const updateFileConfig = useCallback((data) => setFileConfig((prevState) => ({ ...prevState, ...data })), []);
    const setFilePattern = useCallback((value) => {
        const file = value.replaceAll('*', '');
        if (value.endsWith('*')) {
            updateFileConfig({ filePattern: $t('Starts With'), file });
        }
        if (value.startsWith('*')) {
            updateFileConfig({ filePattern: $t('Ends With'), file });
        }
        if (value.startsWith('*') && value.endsWith('*')) {
            updateFileConfig({ filePattern: $t('Contains'), file });
        }
        if (!value.includes('*')) {
            updateFileConfig({ filePattern: $t('Exact Match'), file });
        }
    }, [updateFileConfig]);
    const setFileConfigData = useCallback((data) => {
        updateFileConfig(data.Attributes);
        setFilePattern(data.Attributes.file);
    }, [updateFileConfig, setFilePattern]);
    /* ******** END STEP 2 STATE AND METHODS ******** */
    /* ******** SET DATA FROM GET API CALL ******** */
    const setDIData = useCallback((data) => {
        updateImportRecords({ Name: data.Name });
        data.Actions.forEach((action) => {
            if (action.type === 'getfile') {
                action.NestedNodes.map(setFileConfigData);
            }
            else if (action.type === 'importrecords' || action.type === 'importusersadv') {
                setImportRecordsData(action);
            }
            else {
                additionalActions.current = [...additionalActions.current, ...[action]];
            }
        });
    }, [setFileConfigData, setImportRecordsData, updateImportRecords]);
    const getDataInterchangeTypes = useCallback(async () => {
        await getDataInterchangeTypeStatues()
            .then((data) => {
            const updatedData = JSON.parse(JSON.stringify(data)).map((item) => ({
                ...item,
                ...{ value: typeMap()[item.type] }
            }));
            setInterchangeTypes(updatedData);
        })
            .catch(handleError);
    }, [handleError]);
    const getSFTPDirectories = useCallback(async () => {
        await getDataInterchanges()
            .then((resp) => {
            const data = JSON.parse(resp.Data);
            return [...data.map(transformInterchangeData)];
        })
            .then((data) => {
            getDataInterchangeDirectories(data).then((resp) => {
                setSFTPPaths([...resp.map(transformSFTPPaths)]);
            });
        })
            .catch(handleError);
    }, [handleError]);
    useEffect(() => {
        if (!didMount.current) {
            setLoading(true);
            getDataInterchangeTypes()
                .then(() => {
                getSFTPDirectories();
            })
                .then(() => {
                if (window.location.search) {
                    // eslint-disable-next-line no-underscore-dangle
                    const PK = getQueryStringParam.__ID;
                    getDataInterchangeConfig(PK).then((data) => {
                        setDIData(data);
                        setLoading(false);
                        // millisecond for UI to load and ref set
                        setTimeout(() => {
                            if (customStepperRef.current) {
                                customStepperRef.current.setAllVerticalsComplete();
                            }
                        }, 1);
                    });
                }
                else {
                    setLoading(false);
                }
            })
                .finally(() => {
                didMount.current = true;
            })
                .catch(handleError);
        }
    }, [setDIData, setLoading, handleError, getDataInterchangeTypes, getSFTPDirectories]);
    /* ******** END SET DATA FROM GET API CALL ******** */
    const isNotificationEmailsValid = () => {
        if (importRecords.notificationemails.length) {
            const emailArray = importRecords.notificationemails.trim().split(',');
            return emailArray.every((email) => validateEmail(email).length === 0);
        }
        return true;
    };
    const isStep1Valid = () => isNotificationEmailsValid() && !!importRecords.table && !!importRecords.Name;
    const isStep2Valid = () => !!fileConfig.path && !!fileConfig.filePattern && !!fileConfig.file;
    const isStep3Valid = () => {
        const mappedData = importRecords.mapData.map((item) => item.CertifyField.trim());
        const targetValues = JSON.parse(JSON.stringify(DIConfig.mappingAction.TargetValues));
        const required = targetValues.filter((item) => {
            let isRequired;
            if (importRecords.table === 'User') {
                // Check if user sync and if Emp ID
                isRequired =
                    (item.Source === 'Emp_EmployeeID' && importRecords.key === 'Emp_EmployeeID') ||
                        (item.Source === 'Dept_Name' && !!importRecords.keyondeptname);
                if (item.Source === 'Dept_Code' && !!importRecords.keyondeptname) {
                    // eslint-disable-next-line no-param-reassign
                    item.Required = false;
                }
            }
            else {
                // If not User Sync check if Key is not Name
                isRequired = importRecords.key === item.Source;
            }
            if (isRequired) {
                return { ...item, ...{ Required: true } };
            }
            return item.Required;
        });
        const hasAllRequireds = required.filter((item) => !mappedData.includes(item.Source));
        return hasAllRequireds.length === 0;
    };
    const mapFileToTable = (list) => {
        const rowsToArray = list.split(/\n+/);
        const re = importRecords.delimiter;
        const columnsToArray = rowsToArray.map((val) => {
            // if using comma, replace comma not in quotes with pipe, remove quotes, then split
            if (re === ',') {
                const replace = /(,)(?=(?:[^"]|"[^"]*")*$)/;
                const regex = new RegExp(replace, 'g');
                const newVal = val.replace(regex, '|').replaceAll('"', '');
                return newVal.split('|');
            }
            return val.replaceAll('"', '').split(re);
        });
        const columnsWithHeaders = JSON.parse(JSON.stringify(columnsToArray));
        if (importRecords.header) {
            columnsToArray.shift();
        }
        const mapData = columnsToArray[0].map((column, index) => {
            // Check if there is current mapData, if so, use that for each matching index
            const match = importRecords?.mapData.find(({ id }) => id === index);
            if (match) {
                return {
                    ...match,
                    ...{
                        Example: columnsToArray[0][match.id],
                        ExampleData: columnsWithHeaders.map((col) => col[match.id])
                    }
                };
            }
            return {
                id: index,
                Column: mapIndexToAlphabet(index),
                Example: column,
                CertifyField: ' ',
                ExampleData: columnsWithHeaders.map((col) => col[index])
            };
        });
        updateImportRecords({ mapData });
    };
    const uploadFile = (file) => {
        readFile(file)
            .then((data) => mapFileToTable(data))
            .catch(handleError);
    };
    const getKeyTypes = () => {
        const hasKey = DIConfig?.mappingAction?.Attributes &&
            DIConfig?.mappingAction?.Attributes.filter((item) => item.Name === 'Record Key')
                .length > 0;
        if (hasKey) {
            return DIConfig?.mappingAction?.TargetValues.filter((item) => item.Key === true);
        }
        return [];
    };
    const steps = [
        {
            step: '1',
            label: 'Settings',
            forwardNavProps: { variant: 'outlined', size: 'small', disabled: !isStep1Valid() },
            forwardNavLabel: 'Continue',
            valid: isStep1Valid,
            content: (_jsx(CorpDataInterchangeConfigStep1, { interchangeTypes: interchangeTypes, keyTypes: getKeyTypes(), updateFormData: updateImportRecords, formData: importRecords, emailHelperText: isNotificationEmailsValid()
                    ? ''
                    : $t('One or more email addresses is incorrect.'), attributes: DIConfig?.mappingAction?.Attributes || [] }))
        },
        {
            step: '2',
            label: 'SFTP Configurations',
            forwardNavProps: { variant: 'outlined', size: 'small', disabled: !isStep2Valid() },
            forwardNavLabel: 'Continue',
            valid: isStep2Valid,
            content: (_jsx(CorpDataInterchangeConfigStep2, { sftpPaths: sftpPaths, filePatterns: [
                    $t('Starts With'),
                    $t('Ends With'),
                    $t('Contains'),
                    $t('Exact Match')
                ], updateFormData: updateFileConfig, formData: fileConfig }))
        },
        {
            step: '3',
            label: 'File Upload & Review',
            valid: isStep3Valid,
            content: (_jsx(CorpDataInterchangeConfigStep3Provider, { data: importRecords, mappingValues: DIConfig?.mappingAction?.TargetValues, updateFileData: updateImportRecords, children: _jsx(CorpDataInterchangeConfigStep3, { uploadFile: uploadFile }) }))
        }
    ];
    const isValid = () => steps.every((item) => item.valid());
    /* ******** SAVE METHODS ******** */
    const configureFileFromPattern = () => {
        if (fileConfig.filePattern === $t('Starts With')) {
            return `${fileConfig.file}*`;
        }
        if (fileConfig.filePattern === $t('Ends With')) {
            return `*${fileConfig.file}`;
        }
        if (fileConfig.filePattern === $t('Contains')) {
            return `*${fileConfig.file}*`;
        }
        return fileConfig.file;
    };
    const configureFileData = (data) => {
        const action = JSON.parse(JSON.stringify(data));
        action.Attributes = { type: 'getfile' };
        action.NestedNodes = [
            {
                Name: 'source',
                Attributes: {
                    host: fileConfig.host,
                    file: configureFileFromPattern(),
                    path: fileConfig.path
                }
            }
        ];
        return action;
    };
    const configureMappedData = () => {
        const nestedNodes = [];
        importRecords.mapData.forEach((item) => {
            if (item.CertifyField.trim()) {
                const attributes = configureAdditionalAttribute(item?.AdditionalAttribute);
                const nodeObj = {
                    Name: 'map',
                    Attributes: {
                        sourceindex: item.id,
                        target: item.CertifyField,
                        ...attributes
                    }
                };
                nestedNodes.push(nodeObj);
            }
        });
        return nestedNodes;
    };
    const configureExtraAttributes = (attributes) => {
        const importRecordsTables = 'Dept|EmpUDF1|EmpUDF2|EmpUDF3|EmpUDF4|EmpUDF5|ExpRptUDF1|ExpRptUDF2|ExpRptUDF3|ExpRptUDF4|ExpRptUDF5';
        if (importRecords.table === 'User') {
            return {
                ...attributes,
                ...{
                    advancednotifications: true,
                    AutoPromoteApprovers: true,
                    downloadpreview: true
                }
            };
        }
        if (importRecordsTables.includes(importRecords.table)) {
            return {
                ...attributes,
                ...{
                    fieldsinquotes: true,
                    log: true
                }
            };
        }
        return attributes;
    };
    const configureImportRecordsData = (data) => {
        const action = JSON.parse(JSON.stringify(data));
        action.type = importRecords.table === 'User' ? 'importusersadv' : 'importrecords';
        const attributes = JSON.parse(JSON.stringify(importRecords));
        action.Attributes = configureExtraAttributes(attributes);
        delete action.Attributes.mapData;
        action.NestedNodes = configureMappedData();
        return action;
    };
    const configureDataForSave = () => {
        const config = new DataInterchangeConfig({ Name: importRecords.Name });
        let actions = [];
        config.Actions.forEach((action) => {
            if (action.type === 'getfile') {
                actions.push(configureFileData(action));
            }
            else if (action.type === 'importrecords') {
                actions.push(configureImportRecordsData(action));
            }
            else if (action.type === 'archivefile' && !window.location.search) {
                actions.push(action);
            }
        });
        actions = [...actions, ...additionalActions.current];
        config.Actions = actions;
        return config;
    };
    const onSave = () => {
        if (customStepperRef.current) {
            customStepperRef.current.continueForwardNavigation(steps.length - 1, steps.at(-1).valid());
        }
        if (steps.at(-1).valid()) {
            setSaving(true);
            applyLoadingState(30000);
            // eslint-disable-next-line no-underscore-dangle
            const editPK = getQueryStringParam.__ID;
            const config = configureDataForSave();
            if (window.location.search) {
                updateDataInterchangeConfig(config)
                    .then(() => {
                    window.location.href = `./CorpDataInterchange.aspx?__ID=${editPK}`;
                })
                    .catch(handleError);
            }
            else {
                createDataInterchangeConfig(config)
                    .then((PK) => {
                    window.location.href = `./CorpDataInterchange.aspx?__ID=${PK}&NEW=new`;
                })
                    .catch(handleError);
            }
        }
    };
    /* ******** END SAVE METHODS ******** */
    return (_jsxs(SingleColumnLayout, { children: [_jsx(CorpDataInterchangeConfigModal, { open: showModal, onClose: () => setShowModal(false), onConfirm: () => {
                    window.location.href = './CorpDataInterchange.aspx';
                } }), _jsx(Typography, { variant: "h4", children: window.location.search
                    ? $t('Data Interchange Editing')
                    : $t('Create a new Data Interchange') }), _jsxs(Typography, { variant: "subtitle2", children: [$t('Only a select set of interchange types are supported by this tool. '), _jsx(Link, { href: "https://info.emburse.com/preference-center.html", target: "_blank", children: $t('Sign up') }), $t(' for release notes for further updates.')] }), _jsx(Button, { variant: "text", color: "primary", startIcon: _jsx(Icon, { iconUrl: LaunchIcon }), onClick: () => window.open('https://help.certify.com/hc/en-us/articles/17233192558349', '_blank'), children: $t('Help Center Documentation') }), _jsx(Spacer, { size: 16 }), loading && _jsx(Skeleton, { height: 800, width: 700, style: { transform: 'none' } }), !loading && (_jsx(CustomStepper, { currentStep: 0, isVertical: true, verticalSteps: steps, useButtonLabel: true, useForwardNav: true, ref: customStepperRef })), _jsx(Spacer, { size: 25 }), _jsxs(GridRow, { spacing: 2, children: [_jsx(GridItem, { children: _jsx(Button, { variant: "outlined", disabled: loading || saving, onClick: () => setShowModal(true), children: $t('Cancel') }) }), _jsx(GridItem, { children: _jsx(Button, { disabled: !isValid() || loading || saving, onClick: onSave, children: $t('Save') }) })] })] }));
}
export default CorpDataInterchangeConfig;
